import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, RefreshCw, Mail, Phone, CheckCircle, ExternalLink } from 'lucide-react';
import { exportToCSV, formatDate } from '../../lib/admin/utils';

const STATUS_META = {
  new_campaign_lead:         { label: 'New Lead', className: 'bg-yellow-100 text-yellow-800' },
  awaiting_contact:          { label: 'Awaiting Contact', className: 'bg-blue-100 text-blue-800' },
  awaiting_wine_confirmation:{ label: 'Awaiting Wine', className: 'bg-indigo-100 text-indigo-800' },
  awaiting_payment:          { label: 'Awaiting Payment', className: 'bg-orange-100 text-orange-800' },
  referral_pending:          { label: 'Referral Pending', className: 'bg-purple-100 text-purple-800' },
  referral_matched:          { label: 'Referral Matched', className: 'bg-purple-200 text-purple-900' },
  discount_confirmed:        { label: 'Discount Confirmed', className: 'bg-green-100 text-green-800' },
  order_confirmed:           { label: 'Order Confirmed', className: 'bg-emerald-100 text-emerald-900' },
  completed:                 { label: 'Completed', className: 'bg-emerald-200 text-emerald-900' },
  expired:                   { label: 'Expired', className: 'bg-gray-200 text-gray-700' },
  cancelled:                 { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

const ALL_STATUSES = Object.keys(STATUS_META);

const CampaignTab = ({
  campaignOrders,
  campaignStats,
  loading,
  onRefresh,
  onStatusChange,
  onApproveCredit,
}) => (
  <div className="space-y-4" data-testid="campaign-tab-content">
    <div className="flex justify-between items-center flex-wrap gap-2">
      <h2 className="text-xl font-semibold text-gray-900">FF Campaign — August Wine Event</h2>
      <div className="flex gap-2">
        <Button onClick={onRefresh} variant="outline" size="sm" data-testid="campaign-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
        <Button
          onClick={() => exportToCSV(campaignOrders, 'campaign-orders')}
          className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900"
          size="sm"
          data-testid="campaign-export-btn"
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
    </div>

    {/* Campaign stats strip */}
    {campaignStats && (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatMini label="Total Leads" value={campaignStats.total} />
        <StatMini label="15-btl" value={campaignStats.bottleQtyCounts?.[15] || 0} />
        <StatMini label="30-btl" value={campaignStats.bottleQtyCounts?.[30] || 0} />
        <StatMini label="Referrals Matched" value={campaignStats.referralsMatched} />
        <StatMini label="Credit Pending" value={campaignStats.creditPending} accent="text-orange-600" />
        <StatMini label="Est. Revenue" value={`$${(campaignStats.estimatedRevenue || 0).toFixed(0)}`} />
      </div>
    )}

    {loading ? (
      <div className="text-center py-12">Loading...</div>
    ) : campaignOrders.length === 0 ? (
      <Card><CardContent className="py-12 text-center text-gray-500" data-testid="campaign-empty">
        No campaign leads yet
      </CardContent></Card>
    ) : (
      <div className="space-y-3">
        {campaignOrders.map((o) => {
          const meta = STATUS_META[o.status] || { label: o.status, className: 'bg-gray-100' };
          const isTerminal = ['completed', 'cancelled', 'expired'].includes(o.status);
          const showApprove = o.referralStatus === 'referral_matched' && !o.creditIssued;
          return (
            <Card
              key={o.orderId || o.referralCode}
              className="border-l-4 border-l-[#722F37] hover:shadow-md transition-shadow"
              data-testid="campaign-order-card"
            >
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="font-semibold">{o.firstName} {o.lastName}</span>
                      <Badge className={meta.className} data-testid={`campaign-status-${o.orderId}`}>{meta.label}</Badge>
                      {o.creditIssued && <Badge className="bg-green-100 text-green-800">${o.creditAmount || 25} credit issued</Badge>}
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-500" /><a href={`mailto:${o.email}`} className="text-blue-600 hover:underline">{o.email}</a></div>
                      <div className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-500" /><a href={`tel:${o.phone}`} className="text-blue-600 hover:underline">{o.phone}</a></div>
                      <div className="text-xs text-gray-500">Prefer {o.preferredContact}{o.preferredContactTime ? ` · ${o.preferredContactTime}` : ''}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm">
                      <div><strong>{o.bottleQuantity} bottles</strong> · {o.wineCategory} · {o.sweetness}</div>
                      {o.specificWine && <div className="text-gray-600">Wine: {o.specificWine}</div>}
                      {o.flavourNotes && <div className="text-gray-600">Notes: {o.flavourNotes}</div>}
                      {o.comments && <div className="text-gray-600">Comments: {o.comments}</div>}
                      <div className="text-xs text-gray-500 mt-1">Bottles needed: {o.bottlesRequired ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="line-through text-gray-500">${Number(o.regularPrice || 0).toFixed(2)}</span>
                      <span className="ml-2 font-bold text-[#722F37]">${Number(o.promoPrice || 0).toFixed(2)}</span>
                      <span className="ml-2 text-xs text-gray-500">({Math.round(Number(o.campaignDiscount ?? 15))}% off)</span>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">Referral</div>
                    <div className="text-sm">
                      <div>Code: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{o.referralCode}</code></div>
                      <div className="text-gray-600">Opened: {o.referralOpenedCount || 0}x</div>
                      {o.referringCustomer && <div className="text-gray-600">Referred by: {o.referringCustomer}</div>}
                      {o.referralStatus && <div className="text-purple-700">{o.referralStatus}</div>}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">Submitted: {formatDate(o.createdAt)}</div>
                  </div>
                </div>

                <div className="border-t mt-3 pt-3 flex flex-wrap items-center gap-2">
                  {!isTerminal && (
                    <Select value="" onValueChange={(v) => v && onStatusChange(o.orderId, v)}>
                      <SelectTrigger className="h-9 w-[220px] text-xs" data-testid={`campaign-status-picker-${o.orderId}`}>
                        <SelectValue placeholder="Update status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_STATUSES.filter((s) => s !== o.status).map((s) => (
                          <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {showApprove && (
                    <Button
                      size="sm"
                      onClick={() => onApproveCredit(o.orderId)}
                      data-testid={`campaign-approve-credit-${o.orderId}`}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-1.5" />
                      Approve $25 Credit
                    </Button>
                  )}
                  {isTerminal && <span className="text-xs text-gray-400 italic ml-auto">Terminal — no changes</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    )}
  </div>
);

const StatMini = ({ label, value, accent = 'text-gray-900' }) => (
  <Card>
    <CardContent className="pt-4 pb-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    </CardContent>
  </Card>
);

export default CampaignTab;
